<script lang="ts" setup>
	import type { mediaType } from './camera.vue'

	const props = defineProps<{
		format?: string
		tip?: string
		serverUpload?: boolean
	}>()

	const emits = defineEmits<{
		(event: 'update:file', param: mediaType): void
		(event: 'update:value', param: string): void
	}>()

	const imageInput = useTemplateRef('imageInput')
	const uploading = shallowRef(false)
	const { uploadImage, openFile } = useFileExplorer()
	const file = defineModel<mediaType | null>('file')
	const value = defineModel<string | null>('value')

	const openExplorer = () => openFile(imageInput)
	const getFile = (e: Event) => {
		resolve(async () => {
			const result = await uploadImage(e, props.serverUpload, props.format)
			if (!result) {
				return useNotification().alert({
					status: 400,
					message: 'File upload failed'
				})
			}

			if (result.value.blob) {
				const blobFile = result.value.blob as File
				file.value = {
					blob: blobFile,
					src: result.value.src || ''
				}
				value.value = result.value?.src || ''
			}
		}, uploading).catch(e => {
			useNotification().alert({
				message: e,
				status: 400
			})
		})
	}
	const reUpload = () => {
		file.value = null
		value.value = null
		openExplorer()
	}
</script>

<template>
	<div class="relative">
		<media-placeholder
			:is-empty="file?.src === null || !value"
			icon-name="gallery-add"
			icon-dir="media"
			caption="Upload an image"
			@open="openExplorer"
		>
			<template #empty>
				<div v-if="uploading" class="spin absolute mx-auto">
					<a-spin size="large" />
				</div>
			</template>
			<img alt="upload" :src="value || String(file?.src)" class="w-full rounded-lg object-cover" loading="lazy" />
		</media-placeholder>

		<input
			ref="imageInput"
			class="absolute -z-30 hidden"
			type="file"
			:accept="format || '.jpg,.png'"
			@change="getFile"
		/>
		<div v-if="value || file?.src" class="flex justify-center">
			<button
				type="button"
				class="default-btn primary-border group mb-12 mt-8 w-full max-w-[218px] justify-center gap-x-2 rounded-lg font-medium"
				@click="reUpload"
			>
				<div
					class="flex h-[24px] w-[24px] flex-none items-center justify-center rounded-lg border border-primary-2 group-hover:border-white"
				>
					<lazy-tools-icon
						dir="other"
						icon="renew-plan"
						:height="13"
						:width="13"
						path-class="group-hover:fill-white fill-primary-2 "
					/>
				</div>
				Re-upload Photo
			</button>
		</div>

		<media-tip
			v-show="!value && !file?.src"
			class="my-8"
			:note="tip || 'Upload a clear and professional image of dependant in good lighting'"
		/>
	</div>
</template>

<style scoped>
	.spin :deep(.ant-spin-dot-item) {
		@apply bg-[#506661];
	}
</style>
